import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import {
    IconBanOutline,
    IconCheckCircleFilled,
    IconExclamationTriangleFilled
} from '@iwoca/icons';
import { AlertBox } from '@iwoca/orion';
import {
    getCurrentPathname,
    isLinkingOpenBankingOnly
} from '@iwRouter/LocationHelper';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';

import { AlertStatusTypes } from './TrueLayer';
import {
    TRUELAYER_CANCEL,
    TRUELAYER_FAILURE,
    TRUELAYER_SUCCESS
} from './truelayer.constants';
import styles from './trueLayer.module.css';

export const TruelayerAlertBox = ({
    status,
    handleFailureButtonClick,
    handleCancelButtonClick
}: {
    status: AlertStatusTypes;
    handleFailureButtonClick: () => void;
    handleCancelButtonClick: () => void;
}) => {
    const showUploadBankStatementsButton = !isLinkingOpenBankingOnly();

    useEffect(() => {
        const urlData = {
            url: getCurrentPathname()
        };
        if (status === TRUELAYER_SUCCESS) {
            tracking.ampli.openBankingLinkedSuccessfully(urlData);
        } else if (status === TRUELAYER_CANCEL) {
            tracking.ampli.openBankingLinkingCancelled(urlData);
        } else {
            tracking.ampli.openBankingLinkingFailed(urlData);
        }
    }, [status]);

    if (status === TRUELAYER_SUCCESS) {
        return (
            <AlertBox
                variant={status}
                className={styles.alertBox}
                data-testid="alertbox"
            >
                <IconCheckCircleFilled
                    width={20}
                    height={20}
                    className={styles.successAlertBoxIcon}
                />
                <span
                    className={cn(
                        styles.alertBoxContent,
                        styles.successAlertBoxTextContent
                    )}
                >
                    <FormattedMessage
                        id="_.true_layer.linked"
                        defaultMessage="Great – you've linked your bank account."
                    />
                </span>
            </AlertBox>
        );
    }

    if (status === TRUELAYER_CANCEL) {
        return (
            <AlertBox
                variant={status}
                className={styles.alertBox}
                data-testid="alertbox"
            >
                <IconBanOutline
                    width={20}
                    height={20}
                    className={styles.cancelAlertBoxIcon}
                />
                <span
                    className={cn(
                        styles.alertBoxContent,
                        styles.cancelAlertBoxTextContent
                    )}
                >
                    <span className={styles.bold}>
                        <FormattedMessage
                            id="_.true_layer.no_permission"
                            defaultMessage="We didn't get your permission."
                        />
                    </span>{' '}
                    {showUploadBankStatementsButton && (
                        <button
                            type="button"
                            data-testid="uploadBankStatements"
                            onClick={handleCancelButtonClick}
                            className={styles.link}
                        >
                            <FormattedMessage
                                id="_.true_layer.no_permission_button"
                                defaultMessage="Upload your bank statements instead?"
                            />
                        </button>
                    )}
                </span>
            </AlertBox>
        );
    }

    return (
        <AlertBox
            variant={TRUELAYER_FAILURE}
            className={styles.alertBox}
            data-testid="alertbox"
        >
            <IconExclamationTriangleFilled
                width={20}
                height={20}
                className={styles.failureAlertBoxIcon}
            />
            <span
                className={cn(
                    styles.alertBoxContent,
                    styles.cancelAlertBoxTextContent
                )}
            >
                <span className={styles.bold}>
                    <FormattedMessage
                        id="_.true_layer.failed"
                        defaultMessage="Hmm, that didn't work."
                    />
                </span>{' '}
                <span className={styles.light}>
                    {showUploadBankStatementsButton && (
                        <FormattedMessage
                            id="_.true_layer.try_again"
                            defaultMessage="Try again or {button} instead."
                            values={{
                                button: (
                                    <button
                                        type="button"
                                        data-testid="uploadBankStatements"
                                        onClick={handleFailureButtonClick}
                                        className={styles.link}
                                    >
                                        <FormattedMessage
                                            id="_.true_layer.try_again_button"
                                            defaultMessage="upload your bank statements"
                                        />
                                    </button>
                                )
                            }}
                        />
                    )}
                </span>
            </span>
        </AlertBox>
    );
};
